import React, { ReactNode, useContext } from 'react';
import { AppContext, GlobalUserDetails } from '../app.tsx';
import PdfModal from './pdf-modal.tsx';
import { generatePdfImages } from '../summary/summary-step-two.tsx';
import { CameraController } from '../../camera-controller.ts';
import { Configuration } from '../../data/configuration.js';
import { CloudData } from '../../data/cloud-data.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface SaveSpecificationModalProps {
	cameraController: CameraController;
	setIsDisabled: (shouldDisable: boolean) => void;
	trigger: (openModal: () => void) => ReactNode;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

/**
 * Wraps the PDF modal together with the submit handler that saves the configuration and emails the
 * specification. The caller supplies only the trigger, so the same action can be offered from more
 * than one place (the summary's 'Save Specification' button, and the restart control on the vehicle
 * panel) without the handler being written twice.
 */
export default function SaveSpecificationModal(props: SaveSpecificationModalProps) {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// handlers.

	const handleSubmitPdf = async (userDetails: GlobalUserDetails, userConsented: boolean, optinSelected: boolean) => {

		// ensure email exists.
		if (!userDetails.email) return false;

		// disable UI while generating PDF.
		props.setIsDisabled(true);

		// save config to get the short code.
		const savedConfig = await CloudData.saveConfiguration();
		if (!savedConfig || savedConfig.shortcode.length === 0) {
			props.setIsDisabled(false);
			return false;
		}
		const shortCode = savedConfig.shortcode;

		// generate the pdf images.
		const imageBlobs = await generatePdfImages(props.cameraController);
		if (!imageBlobs || imageBlobs.length === 0) {
			props.setIsDisabled(false);
			return false;
		}

		// set true to save images to disc and disable posting & PDF generation.
		const isDebugMode = false;
		if (isDebugMode) {
			downloadImageBlobs(imageBlobs, 'pdf-camera-view');
		}
		else {

			// generate the PDF url.
			await CloudData.generatePdf(shortCode, imageBlobs, userDetails.email, userDetails?.firstName, userDetails?.lastName);

			// post the lead via the 'send to dealer' mechanism.
			await CloudData.sendToDealer(
				{ ...userDetails, country: 'N/A' },
				savedConfig.shortcode,
				null,
				Configuration.pdfDownloadLeadId,
				Configuration.totalCost,
				userConsented,
				optinSelected
			);
		}

		// re-enable the UI.
		props.setIsDisabled(false);
		return true;
	}

	// return the HTML.
	return (
		<PdfModal
			onSubmit={handleSubmitPdf}
			userDetails={context.globalUserDetails}
			updateUserDetails={context.updateGlobalUserDetails}
			trigger={props.trigger}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

/**
 * Downloads multiple blobs as JPG files.
 * Used for debugging "download PDF images" functionality.
 */
export const downloadImageBlobs = (blobs: Blob[], baseFilename: string = 'camera-view', delayMs: number = 300) => {
	blobs.forEach((blob, index) => {
		setTimeout(() => {
			const filename = `${baseFilename}-${index + 1}.jpg`;
			downloadBlob(blob, filename);
		}, index * delayMs);
	});
};

/**
* Downloads a blob as a file.
*/
const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};
